import { classNames } from "primereact/utils";
import styles from "./DefaultInputField.module.scss"; // Reusing your existing styles
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../../../../AppContext";
import { AppTooltip } from "../../../tooltip/AppTooltip";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { ISelectFieldModel } from "../dropdown/models/selectFieldModel";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface ISuggestInputField {
  className?: string;
  name: string;
  tooltip?: string;
  placeholder: string;
  label?: string;
  required?: boolean;
  width?: string;
  minWidth?: string;
  disabled?: boolean;
  autofocus?: boolean;
  suggestions: ISelectFieldModel[] | undefined;
  onSelect: (selectedItemId: number) => void;
}

export const SuggestInputField = ({
  className,
  name,
  tooltip,
  placeholder,
  label,
  required = false,
  width,
  minWidth,
  disabled,
  autofocus = false,
  suggestions = [],
  onSelect,
}: ISuggestInputField) => {
  const { control } = useFormContext();
  const { isDesktopOrTablet } = useAppContext();
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [justSelected, setJustSelected] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const fieldValue = useWatch({ name, control });

  // Filter suggestions based on input value and exclude exact matches
  const filteredSuggestions = useMemo(() => {
    return suggestions?.filter((item) => {
      const inputValue = (fieldValue || "").toLowerCase();
      const itemName = item.name.toLowerCase();
      return itemName.includes(inputValue) && itemName !== inputValue; // Exclude exact matches
    });
  }, [suggestions, fieldValue]);

  useEffect(() => {
    if (autofocus) {
      inputRef.current?.focus();
    }
  }, [autofocus]);

  useEffect(() => {
    // Reset selected index when suggestions change
    setSelectedIndex(-1);
  }, [filteredSuggestions]);

  useEffect(() => {
    setShowSuggestions(
      isFocused &&
        !!suggestions &&
        !!filteredSuggestions &&
        filteredSuggestions.length > 0 &&
        (fieldValue || "").length > 0 &&
        !justSelected // Hide suggestions immediately after selection
    );
  }, [
    isFocused,
    filteredSuggestions,
    fieldValue,
    suggestions,
    justSelected,
  ]);

  const onFocus = () => {
    setIsFocused(true);
  };

  const onBlur = (e: React.FocusEvent) => {
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(e.relatedTarget as Node)) {
        setIsFocused(false);
        setShowSuggestions(false);
      }
    }, 150);
  };

  const onContainerClick = () => {
    inputRef.current?.focus();
  };

  if (!width) {
    if (isDesktopOrTablet) {
      width = "600px";
    } else {
      width = dvSize("80dvw");
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const handleSuggestionClick = (suggestion: ISelectFieldModel) => {
          field.onChange(suggestion.name); // Set the field value
          onSelect(suggestion.id); // Trigger auto-fill callback
          setJustSelected(true); // Mark as just selected to hide suggestions
          setShowSuggestions(false);
          setSelectedIndex(-1);
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          field.onChange(e); // Normal react-hook-form onChange
          // Reset the "just selected" flag when user starts typing
          if (justSelected) {
            setJustSelected(false);
          }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (!showSuggestions || !filteredSuggestions) return;

          switch (e.key) {
            case "ArrowDown":
              e.preventDefault();
              setSelectedIndex((prev) =>
                prev < filteredSuggestions.length - 1 ? prev + 1 : prev
              );
              break;
            case "ArrowUp":
              e.preventDefault();
              setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
              break;
            case "Enter":
              e.preventDefault();
              if (selectedIndex >= 0 && filteredSuggestions[selectedIndex]) {
                handleSuggestionClick(filteredSuggestions[selectedIndex]);
              }
              break;
            case "Escape":
              setShowSuggestions(false);
              setSelectedIndex(-1);
              inputRef.current?.blur();
              break;
          }
        };

        return (
          <div
            style={{
              width: width,
              minWidth: minWidth,
              maxWidth: width,
              position: 'relative',
              zIndex: showSuggestions ? 9999 : 'auto'
            }}
            className={classNames(className, styles.root, {
              [styles.root_disabled]: disabled,
            })}
            onClick={onContainerClick}
          >
            <RenderWhen condition={!!label}>
              <div
                className={classNames(styles.root_label, {
                  [styles.root_label_interacted_with]:
                    isFocused ||
                    (field.value && field.value.length > 0) ||
                    (field.value !== null && field.value !== ""),
                  [styles.root_label_error]: !!fieldState.error?.message,
                })}
              >
                {label}
                {required ? "*" : ""}
                <AppTooltip content={tooltip} />
              </div>
            </RenderWhen>

            <div
              className={styles.root_input_container}
              style={{ position: "relative" }}
            >
              <input
                type="text"
                ref={inputRef}
                name={name}
                className={styles.root_input}
                placeholder={placeholder}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                value={field.value ?? ""}
                autoComplete="off"
              />

              {/* Suggestions Dropdown */}
              <RenderWhen condition={showSuggestions}>
                <div
                  ref={suggestionsRef}
                  className={styles.suggestions_dropdown}
                >
                  {filteredSuggestions?.map((suggestion, index) => (
                    <div
                      key={suggestion.id}
                      className={classNames(styles.suggestion_item, {
                        [styles.suggestion_item_selected]:
                          index === selectedIndex,
                      })}
                      onMouseDown={(e) => e.preventDefault()} // Prevent blur
                      onClick={() => handleSuggestionClick(suggestion)}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      {suggestion.name}
                    </div>
                  ))}
                </div>
              </RenderWhen>
            </div>
          </div>
        );
      }}
    />
  );
};
